import { Route, Redirect, BrowserRouter as Router } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { NotificationContainer, NotificationManager } from 'react-notifications';
import Intercom from '@intercom/messenger-js-sdk';
import userStore from './Stores/User';
import { StripeProvider } from './StripeProvider';
import { observer } from 'mobx-react';
import OneGiftLogoSquare from './Components/OneGiftLogoSquare';
import ProfileIcon from './Components/ProfileIcon';
import TopNavBar from './Components/TopNavBar';
import mixpanel from 'mixpanel-browser';
import { setTrackingParams } from './utils/trackingParams';
import performanceMonitor from './utils/performanceMonitor';

import Bye from './Pages/Bye';
import Share from './Pages/Share';
import About from './Pages/About';
import Contact from './Pages/Contact';
import Signin from './Pages/Signin';
import ResetPassword from './Pages/ResetPassword';
import RequestPasswordReset from './Pages/RequestPasswordReset';
import PreferredVoices from './Pages/PreferredVoices';
import Newsletter from './Pages/Newsletter';
import Embed from './Pages/Embed';
import history from './history';
import PaymentSuccess from './Pages/PaymentSuccess';
import Onboarding from './Pages/Onboarding';
import Drawabox from './Pages/Drawabox';
import SetupNew from './Pages/SetupNew';
import InstantAudioPreview from './Pages/SetupNew/Components/InstantAudioPreview';
import Ios from './Pages/SetupNew/Components/Ios';
import Android from './Pages/SetupNew/Components/Android';
import Trial from './Pages/Onboarding/Pages/Trial';
import Welcome from './Pages/Onboarding/Pages/Welcome';
import CreateAccount from './Pages/Onboarding/Pages/CreateAccount';
import HowToClip from './Pages/Onboarding/Pages/HowToClip';
import Complete from './Pages/Onboarding/Pages/Complete';
import TrialMobile from './Pages/Onboarding/Pages/TrialMobile';
import ChooseVoice from './Pages/Onboarding/Pages/ChooseVoice';
import PodcastApp from './Pages/Onboarding/Pages/PodcastApp';
import ChoosePlan from './Pages/Onboarding/Pages/ChoosePlan';
import Types from './Pages/Onboarding/Pages/Types';
import Quickstart from './Pages/Onboarding/Pages/Quickstart';
import Quickstart1 from './Pages/Onboarding/Pages/Quickstart1';
import Quickstart2 from './Pages/Onboarding/Pages/Quickstart2';
import Quickstart3 from './Pages/Onboarding/Pages/Quickstart3';
import ContentSwitch from './Pages/ContentSwitch';
import SocialThumbnailTemplate from './Pages/SocialThumbnailTemplate';
import Subscribe from './Pages/Subscribe';
import EditText from './Pages/EditText';
import Upload from './Pages/DiscoverNew/Components/Upload';
import LinkEmail from './Pages/LinkEmail';
import Account from './Pages/Account';
import Shortcuts from './Pages/Shortcuts';
import InstallAndroid from './Pages/InstallAndroid';
import CheckoutForm from './Pages/CheckoutForm';
import TermsOfService from './Pages/TermsOfService';
import PrivacyPolicy from './Pages/PrivacyPolicy';
import DmcaPolicy from './Pages/DmcaPolicy';
import CookieConsent from './Components/CookieConsent';
import CookieManager from './Components/CookieManager';
import FeedLanding from './Pages/FeedLanding';
import FreeAiTextToSpeech from './Pages/Landing/FreeAiTextToSpeech';
import LandingOrganizations from './Pages/Landing/LandingOrganizations';
import LandingAPI from './Pages/Landing/LandingAPI';
import LandingFeatures from './Pages/Landing/LandingFeatures';
import LandingHowItWorks from './Pages/Landing/LandingHowItWorks';
import LandingIntegrations from './Pages/Landing/LandingIntegrations';
import LandingSolutions from './Pages/Landing/LandingSolutions';
import FeedsDirectory from './Pages/FeedsDirectory';
import Settings from './Pages/Settings';
import DynamicRouter from './Components/DynamicRouter';
import ScrollToTop from './Components/ScrollToTop';



const AppStatus = () => {
  const isLocal = window.location.href.includes('http://localhost') || window.location.href.includes('http://192.168.1.66');
  const isStaging = window.location.href.includes('https://dev.audiblogs.com') || window.location.href.includes('https://dev.audioread.com');
  // if (isLocal || isStaging) {
  //   return (
  //     <div
  //       style={{
  //         backgroundColor: isLocal ? 'red' : 'orange',
  //         color: 'white',
  //         textAlign: 'center',
  //         padding: '5px',
  //         fontSize: '14px',
  //         fontWeight: 'bold',
  //         position: 'fixed',
  //         top: 0,
  //         width: '100%',
  //         zIndex: 1001,
  //       }}
  //     >
  //       {isLocal ? 'Local' : 'Staging'}
  //     </div>
  //   );
  // }
  return null;
};

const App = observer(() => {
  const [isCookieManagerOpen, setIsCookieManagerOpen] = useState(false);
  const { user } = userStore;

  useEffect(() => {
    // Disable browser scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Set global black background
    document.body.style.backgroundColor = '#000000';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.backgroundColor = '#000000';
    
    // Add padding for fixed navbar
    if (user) {
      document.body.style.paddingTop = '100px'; // 80px navbar height + 20px margin
    } else {
      document.body.style.paddingTop = '0';
    }

    // Check for partner discount message and display it
    const partnerMessage = localStorage.getItem('partnerDiscountMessage');
    if (partnerMessage) {
      NotificationManager.info(partnerMessage, 'Partner Discount', 5000);
      localStorage.removeItem('partnerDiscountMessage');
    }

    // Capture tracking params on every page load
    setTrackingParams();

    // Initialize performance monitoring
    if (process.env.NODE_ENV === 'production') {
      // Generate performance report after page load
      setTimeout(() => {
        performanceMonitor.generateReport();
      }, 5000);
    }

    // Identify user in Mixpanel if logged in
    if (user?.user_id) {
      mixpanel.identify(user.user_id);
      mixpanel.people.set({
        $email: user.email,
        $name: user.name,
        $created: user.created_at,
        $last_login: new Date().toISOString()
      });
    }

    // Initialize Intercom with hidden launcher - important to start with it hidden
    Intercom({
      app_id: "fduylg2r",
      hide_default_launcher: true
    });

    // Add global function to open cookie manager
    window.openCookieManager = () => setIsCookieManagerOpen(true);
    
    // Ensure Intercom launcher stays hidden
    const ensureIntercomHidden = () => {
      setTimeout(() => {
        if (window.Intercom) {
          try {
            window.Intercom('update', {
              hide_default_launcher: true
            });
          } catch (e) {
            console.error("Error hiding Intercom launcher:", e);
          }
        }
      }, 2000);
    };
    
    ensureIntercomHidden();
    
    return () => {
      // Clean up any custom launcher if it exists
      const customLauncher = document.querySelector('.custom-intercom-launcher');
      if (customLauncher) {
        document.body.removeChild(customLauncher);
      }
    };
  }, [user]);

  return (
    <StripeProvider>
      <AppStatus />
      <Router history={history}>
        <ScrollToTop />
        {user && <TopNavBar />}
        {user && <ProfileIcon />}
        {/* {user && (
          <div style={{ 
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1001 // Higher than AppStatus
          }}>
            <OneGiftLogoSquare />
          </div>
        )} */}
        <Route path="/" exact component={ContentSwitch} />
        <Route path="/playlist" exact>
          <Redirect to="/add" />
        </Route>
        <Route path="/add" exact component={ContentSwitch} />
        <Route path="/search" exact component={ContentSwitch} />
        <Route path="/settings" exact component={Settings} />
        <Route path="/setupnew" exact component={SetupNew} />
        <Route path="/discover" exact component={ContentSwitch} />
        <Route path="/feeds" exact component={FeedsDirectory} />
        <Route path="/PreferredVoices" exact component={PreferredVoices} />
        <Route path="/about" exact>
          <Redirect to="/" />
        </Route>
        <Route path="/contact" exact component={Contact} />
        <Route path="/bye/" exact component={Bye} />
        <Route path="/player/:USER_ID" component={Signin} />
        <Route path="/share/:filename" component={Share} />
        <Route path="/setup/" exact>
          <Redirect to="/" />
        </Route>
        <Route path="/Account" exact component={Account} />

        <Route path="/signin/" exact component={Signin} />
        <Route path="/finishSignin" component={Signin} />
        <Route path="/signin/:USER_ID" component={Signin} />
        <Route path="/request-password-reset" exact component={RequestPasswordReset} />
        <Route path="/reset-password" exact component={ResetPassword} />
        <Route path="/newsletter" exact component={Newsletter} />
        <Route path="/embed" exact component={Embed} />
        <Route path="/payment/success" exact component={PaymentSuccess} />
        <Route path="/onboarding" exact component={Onboarding} />
        <Route path="/setupnew/trial">
          <Redirect to="/onboarding/trial" />
        </Route>
        <Route path="/onboarding/trial" exact component={Trial} />
        <Route path="/onboarding/TrialMobile" exact component={TrialMobile} />
        <Route path="/onboarding/Welcome" exact component={Welcome} />
        <Route path="/onboarding/CreateAccount" exact component={CreateAccount} />
        <Route path="/onboarding/HowToClip" exact component={HowToClip} />
        <Route path="/onboarding/Complete" exact component={Complete} />
        <Route path="/onboarding/ChooseVoice" exact component={ChooseVoice} />
        <Route path="/onboarding/ChoosePlan" exact component={ChoosePlan} />
        <Route path="/onboarding/PodcastApp" exact component={PodcastApp} />
        <Route path="/onboarding/Types" exact component={Types} />
        <Route path="/onboarding/Quickstart" exact component={Quickstart} />
        <Route path="/onboarding/Quickstart/2" exact component={Quickstart2} />
        <Route path="/onboarding/Quickstart/1" exact component={Quickstart1} />
        <Route path="/onboarding/Quickstart/3" exact component={Quickstart3} />

        <Route path="/demo" exact component={InstantAudioPreview} />
        <Route path="/setupnew/ios" exact component={Quickstart2} />
        <Route path="/setupnew/android" exact component={Quickstart2} />
        <Route path="/subscribe" exact component={Subscribe} />
        <Route path="/subscription" exact component={Subscribe} />
        <Route path="/add/text" exact component={EditText} />
        <Route path="/add/file" exact component={Upload} />
        <Route path="/link" exact component={LinkEmail} />
        <Route path="/drawabox" exact component={Drawabox} />
        <Route path="/onboarding/ios" exact component={Shortcuts} />
        <Route path="/onboarding/android" exact component={InstallAndroid} />
        <Route path="/CheckoutForm" exact component={CheckoutForm} />
        <Route path="/terms" exact component={TermsOfService} />
        <Route path="/privacy" exact component={PrivacyPolicy} />
        <Route path="/dmca" exact component={DmcaPolicy} />

        <Route
          path="/SocialThumbnailTemplate"
          exact
          component={SocialThumbnailTemplate}
        />
        <Route path="/feed/:feedId/*" component={FeedLanding} />
        <Route path="/freeaitexttospeech" exact component={FreeAiTextToSpeech} />
        <Route path="/organizations" exact component={LandingOrganizations} />
        <Route path="/api" exact component={LandingAPI} />
        <Route path="/features" exact component={LandingFeatures} />
        <Route path="/how-it-works" exact component={LandingHowItWorks} />
        <Route path="/integrations-and-platforms" exact component={LandingIntegrations} />
        <Route path="/solutions-and-accessibility" exact component={LandingSolutions} />
        <Route
          path="/affiliate"
          exact
          render={() => {
            window.location.href = "https://audioread.tolt.io";
          }}
        />
        
        {/* Dynamic routing for partners, coupons, and referrals - more specific */}
        <Route path="/code/:couponCode" exact component={DynamicRouter} />
        <Route path="/referral/:referralUuid" exact component={DynamicRouter} />
        {/* Only match actual partner names, not random strings */}
        <Route path="/:partnerRoute" exact component={DynamicRouter} />
      </Router>
      <NotificationContainer />
      <CookieConsent />
      <CookieManager
        isOpen={isCookieManagerOpen}
        onClose={() => setIsCookieManagerOpen(false)}
      />
    </StripeProvider>
  );
});

export default App;
